import $ from 'jquery';
import isUndefined from 'lodash/isUndefined';
import {getTemplate} from '../templates';
import {odata, event} from 'linksoft';
import {getDataTableApi} from '../utils';

const log = require('debug')('linksoft:datatables:StringColumn');

export const noValue = '-';

export default class StringColumn {
	_lastFilterValue = '';

	table = null;
	options = null;
	index = 0;
	defaultContent = '';

	/**
	 * DataTable API.
	 * @type {null}
	 */
	get api() {
		return this._api || (this._api = $(this.table).DataTable());
	};

	get column() {
		return this.api.column(this.index);
	};

	get sourceProp() {
		return this.options.data || this.options.name;
	}

	get orderByProp() {
		return this.sourceProp;
	}

	get filterByProp() {
		return this.sourceProp;
	}

	constructor(tableElement, options, index) {
		this.table = tableElement;
		this.options = options;
		this.index = index;
	}

	// ----------- CELL RENDERING -----------------------
	/**
	 * Basic implementation of displaying data. Only used when template option is not set.
	 * @param value
	 * @returns {*|string}
	 */
	displayValue(value) {
		const {isArray} = this.options;
		if (value === null || isUndefined(value) || value === '' || (isArray && !value.length)) return noValue;
		if(isArray) {
			return value.join(', ');
		}
		return value;
	}

	// ------------ EDITOR --------------------------

	/**
	 * Returns input element for text editing.
	 * @param value Original value.
	 * @returns {string} HTML markup.
	 */
	createEditor(value, id) {
		return $(`<input class="table-input" type="text" />`).val(value || '').get(0);
	}

	/**
	 * Returns value from table cell editor input element.
	 * @param cellElement {HTMLElement}
	 * @returns {string}
	 */
	getEditorValue(cellElement) {
		return $(cellElement).find('.table-input').val();
	}

	onEditorCreated(cellElement) {
		cellElement.find('input:first').focus();
	}

	buildPatchData(value) {
		const data = {};
		const src = this.column.dataSrc();
		data[src] = value;
		return data;
	}

	// -------------- FILTERS --------------------

	/**
	 * Extracts value from filter input to be used as actual filter value.
	 */
	getFilterValue() {
		const $target = this.column.filter().find('.table-filter');
		if ($target.length === 2) {
			const val = $($target.get(0)).val() + "~" + $($target.get(1)).val();
			return val !== '~' ? val : '';
		}
		return $target.val() || "";
	}

	/**
	 * Generates OData filter expression for current column filter.
	 */
	getFilterExpression() {
		const fieldName = this.filterByProp;
		const value = this.column.search();
		if(!value) return null;
		return this.buildFilterExpression(fieldName.replace(/\./, '/'), value);
	}

	buildFilterExpression(fieldName, value) {
		return this.options.isArray
			? this.options.useDollarSearch
				? odata.filters.anyStartsOrEndsWith(fieldName, value)
				: odata.filters.anyContains(fieldName, value)
			: this.options.useDollarSearch
				? odata.filters.startsOrEndsWith(fieldName, value)
				: odata.filters.contains(fieldName, value);
	}

	/**
	 * Returns text to be displayed as filtered value. Can differ from actual filtered value,
	 * e.g. lookup column shows name but filters by id.
	 */
	getFilterText() {
		return this.getFilterValue();
	}

	/**
	 * Set filter value to filter input(s).
	 * Override this, to be consistent with createFilter function output.
	 * @param value
	 */
	setFilterValue(value) {
		const inputs = this.column.filter().find('input.table-filter');
		const values = value.split('~', 2);

		if(inputs.length > 0) {
			$(inputs.get(0)).val(values[0] || '');
		}
		if(inputs.length > 1) {
			$(inputs.get(1)).val(values[1] || '');
		}
	}

	/**
	 * Override this to perform custom element focus when filter box opens.
	 */
	setFilterFocus() {
		this.column.filter().find('input:first').focus();
	}

	showFilterButton() {
		return true;
	}

	onFilterRendered() {

	}

	autoCloseFilterBox() {
		return true;
	}

	/**
	 * This is the method which is actually passed to DataTables column option 'render' callback.
	 * @param value
	 * @param type
	 * @param row
	 * @param meta
	 */
	render(value, type, row, meta) {
		const {template, templateArgs, formatString} = this.options;
		if (template) {
			let templateFunc = template;
			if (typeof templateFunc === 'string') {
				templateFunc = getTemplate(templateFunc);
			}
			if (typeof templateFunc === 'function') {
				const e = {
					column: this.column,
					api: this.api,
					meta,
					value,
					type,
					data: row,
					formatString: formatString
				};
				const args = [e, ...(templateArgs || [])];
				return templateFunc.apply(row, args) || noValue;
			}
		}
		return this.displayValue(value, row);
	}

	exportRender (value, row, meta) {
		return this.render(value, 'display', row, meta);
	}

	createWrapper(negativeFilterDisplayElement, negativeFilterCancelClick) {
		return `<div class="filter-wrapper">
	<div class="filter-display">
    <i class="fa fa-remove fa-filter-clear filter-closer" onclick="${negativeFilterCancelClick}"></i>${negativeFilterDisplayElement}
    <div class="filter-string"></div>
		<i class="fa fa-filter filter-opener"></i>
	</div>
	<div class="filter-box-wrap hide">
		<button class="btn btn-grey">
			<i class="fa fa-search"></i>
		</button>
	</div>
</div>`;
	}

	createFilter() {
		return `<input type="text" class="form-control input-sm table-filter" />`;
	}

	/**
	 *
	 */
	renderFilter() {
		if (this.options.searchable === false) return;

		let f = this.createFilter();
		if (!f) return;

		let negativeFilterDisplayElement = '';
		let negativeFilterCancelClick = '';

		if (this.options.negativefilter) {
			const columnName = this.options.data;
			const url = JSON.parse(this.table.dataset.table).ajax.url;
			const controller = (new URL(url)).pathname.split('/')[2]; //this is used tp match negative filter switch and odata request
			const id = `negative-filter-${controller}-${columnName}`; //this is base id of specific negative filter elements; id = hidden element, id-display = indicator element, id-button = switch element
			const urlMatch = `[data-url=&quot;${url}&quot;]`; //quotes are escaped because it's in html attribute enclosed with "
			const claas = `negative-filter-${controller}`; //used to find all negative filters on page
			//this script switches specific negative filtger on/off + changes indication elements
			const onClick = `var $val = $('#${id}${urlMatch}'); this.style.color = ($val.val() == 1 ? 'gray' : 'orange'); $('#${id}-display${urlMatch}').css('display', $val.val() == 1 ? 'none' : 'inline-block'); $val.val($val.val() == 1 ? 0 : 1);`;
			var hint = "Negativn� filtr";
			var btnStyle = "position: absolute; display: inline-block; right: 10px; bottom: 8px; font-weight: bold; color: gray;";
			var displayStyle = "position: absolute; display: none; left: 10px; bottom: 0px; font-weight: bold; color: #0076A8;";
			//this adds switch element after filter input box
			f = f + `<span id="${id}-button" onclick="${onClick}" style="${btnStyle}" title="${hint}" data-url="${url}">&nbsp;&ne;&nbsp;</span>`;
			//this adds hidden element after filter input box
			//class is used to locate negativeFilters for table, data-column is used to identify columnName with active negativeFilter, data-url is used to differeciate between two tables with different parameters
			f = f + `<input type="hidden" name="${id}" class="${claas}" id="${id}" data-column="${columnName}" data-url="${url}" value="0">`;
			//this adds indicator element into wrapper
			negativeFilterDisplayElement = `<span id="${id}-display" style="${displayStyle}" title="${hint}" data-url="${url}">&nbsp;&ne;&nbsp;</span>`;
			//this adds script that is called when filter is canceled - it switches specific negative filter off
			negativeFilterCancelClick = `$('#${id}-button${urlMatch}').css('color', 'gray'); $('#${id}-display${urlMatch}').css('display', 'none'); $('#${id}${urlMatch}').val(0);`;
		}

		const $wrapper = $(this.createWrapper(negativeFilterDisplayElement, negativeFilterCancelClick));
		if (!this.showFilterButton()) {
			$wrapper.find('.filter-box-wrap button').remove();
		}

		const $box = $wrapper.find('.filter-box-wrap');
		$box.prepend(f);

		this.column.filter().html($wrapper);

		this.onFilterRendered();
	}

	/**
	 *  Displays filtered text and updates UI as appropriate
	 */
	displayFilter() {
		const value = this.column.search();
		if (this._lastFilterValue == value) return;

		this._lastFilterValue = value;
		this.setFilterValue(value);

		const text = this.getFilterText();
		const filterCell = this.column.filter();

		// display filter text
		filterCell.find('.filter-string').text(text);
		filterCell.toggleClass('filter-active', !!value);
	}
}

function closeAllFilters() {
	$('.dataTable thead th').removeClass('filter-opened');
	$('.dataTable thead th .filter-box-wrap').addClass('hide');

	log('filter boxes closed');
}

function getRenderer(element) {
	const api = getDataTableApi(element);
	const column = api.columnByFilter(element);
	return column.renderer();
}

function applyFilter(element) {
	const renderer = getRenderer(element);
	// apply filter
	const value = renderer.getFilterValue();
	renderer.column.search(value).draw();
	if(renderer.autoCloseFilterBox()) {
		closeAllFilters();
	}
	return false;
}

// click outside of filter box should close the box
$(document).on('click', function (e) {
	if ($(e.target).closest('.filter-wrapper').length) return;
	closeAllFilters();
});

// clear filter button handler
$(document).on('click', 'table.dataTable .filter-closer', function () {
	getDataTableApi(this).columnByFilter(this).search('').draw();
});

// open filter box
$(document).on('click', 'table.dataTable .filter-wrapper .filter-display', function (e) {
	const $target = $(this);
	if ($(e.target).is('.filter-closer')) return;

	const api = getDataTableApi($target);
	const $th = $target.closest('th');
	const isOpen = $th.is('.filter-opened');
	const renderer = api.columnByFilter($target).renderer();

	closeAllFilters();

	$th.addClass('filter-opened');
	$th.find('.filter-box-wrap').removeClass('hide');

	if (!isOpen) {
		renderer.setFilterFocus();
	}
	log('filter box opened');
});

// apply filter on filter confirm button click
$(document).on('click', 'table.dataTable .filter-wrapper button', function () {
	return applyFilter(this);
});

// apply filter on select value change
$(document).on('change', 'table.dataTable .filter-wrapper select.table-filter', function () {
	return applyFilter(this);
});

// apply filter on press enter
$(document).on('keydown', 'table.dataTable .filter-wrapper .filter-box-wrap', function (e) {
	if (e.which === 13) { // enter
		applyFilter(this);
		return false;
	}
	if (e.keyCode === 27) { // esc
		getRenderer(this).displayFilter();
		closeAllFilters();
	}
	return true;
});

// // updates UI when search event occurs
// $(document).on('search.dt', function(e, settings){
// 	log('table search event triggered');
// 	const api = $.fn.dataTable.Api(settings);
//
// 	// TODO: api.columns().search();
// });

// display filter value / text on headerCallback
event.on('linksoft.datatables.headerCallback', (e, data) => {
	const api = getDataTableApi(data);
	api.columns().every(function () {
		this.renderer().displayFilter();
	});

	log('table header filters displayed');
});



// WEBPACK FOOTER //
// ./node_modules/linksoft-datatables/src/renderers/StringColumn.js